import React from "react";
import SVG from "react-inlinesvg";
import Modal from "react-modal";
import { t } from "ttag";

import { getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import iconCheckCircleTeal from "../../../../img/icons/check-circle-teal.svg";
import iconXCircleRed from "../../../../img/icons/x-circle-red.svg";
import { IProduct } from "../../../models/catalogue";
import { IProductID } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { trapFocus } from "../../../utils/keyboardFocus";
import { PointDifferenceRow } from "../../../utils/similarity";
import { MOBILE_WIDTH_THRESHOLD_ALTERNATIVE } from "../constants";
import { defaults } from "../defaults";
import { IPLCOptionPanel, IPLCProductCategorySelector } from "../models";
import { getMostSimilarProduct } from "../product-similarity";
import { setSelectedVariant, undoSelection } from "../reducers";
import {
    previouslySelectedCategoriesSelector,
    selectedCategoriesSelector,
} from "../selectors";

// Load styles
import "./index.scss";

interface IPLCProductMatchModalProps {
    categorySelectors: readonly IPLCProductCategorySelector[];
    optionPanels: readonly IPLCOptionPanel[];
    modalPosition: number | undefined;
}

/**
 * Return a map of product variant IDs to the ID of the root product
 */
const getRootProductLookupTable = (
    rootProducts: readonly IProduct[],
): Map<IProductID, IProductID> => {
    const table = new Map<IProductID, IProductID>();
    rootProducts.forEach((root) => {
        root.children.forEach((child) => {
            table.set(child.id, root.id);
        });
    });
    return table;
};

const BEM_NAME = "plc-product-match-modal";

const DifferenceRow = (props: { row: PointDifferenceRow }) => {
    return (
        <tr>
            <td>
                <span className={`${BEM_NAME}__dimension-name`}>
                    {props.row.dimensionName}
                </span>
                <span
                    className={`${BEM_NAME}__dimension-value ${BEM_NAME}__dimension-value--removing`}
                >
                    {props.row.aDescr}
                </span>
            </td>
            <td>
                <span className={`${BEM_NAME}__dimension-name`}>
                    {props.row.dimensionName}
                </span>
                <span
                    className={`${BEM_NAME}__dimension-value ${BEM_NAME}__dimension-value--adding`}
                >
                    {props.row.bDescr}
                </span>
            </td>
        </tr>
    );
};

const isMobileWidth = (): boolean => {
    const window = getWindow();
    return (window?.innerWidth ?? 0) < MOBILE_WIDTH_THRESHOLD_ALTERNATIVE;
};

export const PLCProductMatchModal = (
    props: IPLCProductMatchModalProps,
): React.ReactNode => {
    const [contentHeight, setContentHeight] = React.useState<
        number | undefined
    >(undefined);
    const contentElemRef = React.useRef<HTMLDivElement>(null);
    const modalElemRef = React.useRef<HTMLDivElement>(null);

    const dispatch = useAppDispatch();

    const rootProducts = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.entities.rootProducts;
    });

    const selectedCategories = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return selectedCategoriesSelector(state);
    });

    const optionValues = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.optionValues;
    });

    const previouslySelectedCategories = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return previouslySelectedCategoriesSelector(state);
    });

    const previouslySelectedOptionValues = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.previousSelectionState?.optionValues || {};
    });

    const onAfterModalOpen = (): void => {
        if (!modalElemRef.current) {
            return;
        }
        setContentHeight(
            contentElemRef.current?.getBoundingClientRect().height,
        );
        trapFocus(modalElemRef.current, { includeExplicitTabindex: true });
    };

    const onCancel = (): void => {
        dispatch(undoSelection());
    };

    const onConfirm = (closestMatchingVariant: IProduct): void => {
        const rootProductLookupTable = getRootProductLookupTable(rootProducts);
        const rootProductID = rootProductLookupTable.get(
            closestMatchingVariant.id,
        );
        const rootProduct = rootProducts.find(
            (root) => root.id === rootProductID,
        );
        if (!rootProduct) {
            throw new Error("Root product for variant is missing!");
        }
        dispatch(
            setSelectedVariant({
                root: rootProduct,
                variant: closestMatchingVariant,
                categorySelectors: props.categorySelectors,
            }),
        );
    };

    const match = getMostSimilarProduct({
        categorySelectors: props.categorySelectors,
        optionPanels: props.optionPanels,
        rootProducts,
        selectedCategories,
        optionValues,
        previouslySelectedCategories,
        previouslySelectedOptionValues,
    });

    if (match === null) {
        return null;
    }

    const matchIsImperfect = match.distance > 0;
    const visibleDiffRows = match.difference.filter((r) => r.showInDiff);
    const hasVisibleDiff = visibleDiffRows.length > 0;

    // If the match is imperfect, but none of the differences are supposed to be shown in the diff
    // table, then just silently select it without opening the modal.
    if (matchIsImperfect && !hasVisibleDiff) {
        onConfirm(match.bestProduct);
    }

    const topPos =
        props.modalPosition && contentHeight
            ? props.modalPosition - contentHeight
            : 0;

    return (
        <Modal
            aria={{ modal: true }}
            className={`${BEM_NAME}__modal`}
            overlayClassName={`${BEM_NAME}__overlay`}
            contentRef={(r) => {
                modalElemRef.current = r;
            }}
            isOpen={matchIsImperfect && hasVisibleDiff}
            onAfterOpen={onAfterModalOpen}
            shouldReturnFocusAfterClose={false}
            bodyOpenClassName="match-modal-open"
            role="dialog"
            style={{
                content: {
                    top: isMobileWidth() ? "auto" : `${topPos}px`,
                    bottom: isMobileWidth() ? "0px" : "auto",
                    right: "0",
                    padding: "0",
                    position: "absolute",
                    overflow: "auto",
                },
                overlay: {
                    backgroundColor: "rgba(0, 0, 0, 0.25)",
                },
            }}
        >
            <div ref={contentElemRef} className={`${BEM_NAME}__content`}>
                <header>
                    <p>
                        {match.betterProductExistsButIsUnavailable
                            ? t`Out of Stock`
                            : t`Changes Required`}
                    </p>
                    <p>
                        {match.betterProductExistsButIsUnavailable
                            ? t`The options you've selected aren't currently in-stock, but a similar configuration is.`
                            : t`The options you've selected aren't available, but a similar configuration is.`}
                    </p>
                </header>
                <div className="table-holder">
                    <table>
                        <thead>
                            <tr>
                                <th>
                                    <SVG src={iconXCircleRed} />
                                    {t`Removing`}
                                </th>
                                <th>
                                    <SVG src={iconCheckCircleTeal} />
                                    {t`Adding closest match`}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleDiffRows.map((row) => (
                                <DifferenceRow
                                    key={row.dimensionName}
                                    row={row}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className={`${BEM_NAME}__buttons`}>
                    <button onClick={onCancel}>{t`Cancel`}</button>
                    <button
                        onClick={() => {
                            onConfirm(match.bestProduct);
                        }}
                    >
                        {t`Confirm`}
                    </button>
                </div>
            </div>
        </Modal>
    );
};
